import type { NavigatorSeriesOptions } from "./navigator";
import {
  navigatorId,
  withNavigatorSeriesOptions,
  navigatorSerieOptions,
  normalizeData,
} from "./navigator";
import { stableColor } from "../..";
import objectDiff from "./objectDiff";
import cloneDeep from "lodash.clonedeep";

function removeCommonElements<T extends string | number>(
  oldA: T[],
  newA: T[],
): [removedA: T[], addedA: T[]] {
  const oldSet = new Set(oldA);
  const newSet = new Set(newA);

  const removedA: T[] = [];
  const addedA: T[] = [];

  for (const num of oldSet) {
    if (!newSet.has(num)) {
      removedA.push(num);
    }
  }

  for (const num of newSet) {
    if (!oldSet.has(num)) {
      addedA.push(num);
    }
  }

  return [removedA, addedA] as [T[], T[]];
}

const findSerie = (chart: Highcharts.Chart, id: string, navigator: boolean) => {
  const _id = navigator ? navigatorId(id) : id;
  const serie = chart.series.find(s => {
    if (!s.options.id) throw new Error("Missing option id");
    return s.options.id === _id;
  });
  if (!serie) {
    // eslint-disable-next-line no-console
    console.warn(`Could not find serie with id ${id}`, {
      chart,
      id,
      _id,
      navigator,
      "chart.series": chart.series.map(s => s.options?.id),
    });
    throw new Error(`Cannot find serie with id ${id}`);
  }
  return serie;
};

const findSerieOptions = (seriesOptions: Highcharts.SeriesOptionsType[], id: string) =>
  seriesOptions.find(so => {
    if (!so.id) throw new Error("Missing option id");
    return so.id === id;
  });

const smartUpdate = (
  name: string,
  chart: Highcharts.Chart,
  { series: seriesOptions, ...options }: Highcharts.Options,
  { series: previousSeriesOptions, ...previousOptions }: Highcharts.Options,
  normalizeNavigatorSeries: boolean,
) => {
  const hasNavigator = Boolean(chart.options.navigator?.enabled);
  const optionsDiff = objectDiff(
    previousOptions as { [k: string]: unknown },
    options as { [k: string]: unknown },
  );

  const optionsChanged = Object.keys(optionsDiff).length;

  const addedSeriesOptions: Highcharts.SeriesOptionsType[] = [];
  const changedSeriesOptions: Highcharts.SeriesOptionsType[] = [];
  const deletedSeriesOptions: Highcharts.SeriesOptionsType[] = [];

  if (!seriesOptions || !previousSeriesOptions) throw new Error("Missing key series in options");

  for (const serieOptions of seriesOptions) {
    if (!serieOptions.id) throw new Error(`Missing options.id`);
    const oldSerieOptions = findSerieOptions(previousSeriesOptions, serieOptions.id);
    if (!oldSerieOptions) {
      addedSeriesOptions.push(serieOptions);
      continue;
    }
    const serieOptionDiff = objectDiff(
      oldSerieOptions as unknown as { [k: string]: unknown },
      serieOptions as unknown as { [k: string]: unknown },
    ) as unknown as Highcharts.SeriesOptionsType;
    if (Object.keys(serieOptionDiff).length)
      changedSeriesOptions.push({ ...serieOptionDiff, id: serieOptions.id });
  }

  for (const oldOptions of previousSeriesOptions) {
    if (!oldOptions.id) throw new Error(`Missing oldOptions.id`);
    const options = findSerieOptions(seriesOptions, oldOptions.id);
    if (!options) deletedSeriesOptions.push(oldOptions);
  }

  const seriesChanged =
    changedSeriesOptions.length || deletedSeriesOptions.length || addedSeriesOptions.length;

  if (!optionsChanged && !seriesChanged) {
    console.debug(`%c[${name}] %cNO-UPDATE`, `color: ${stableColor(name)};`, "color: grey;");
  } else if (optionsChanged && seriesChanged) {
    // full update, cannot update series and options separately (axes references, etc)
    const fullOptions = { series: seriesOptions, ...optionsDiff };
    console.debug(
      `%c[${name}] %cFULL-UPDATE`,
      `color: ${stableColor(name)};`,
      "color: red;",
      Object.keys(fullOptions).join(", "),
    );
    // wipe out series if yAxis and series changes, to avoid all kind of mayhem with navigator series
    // if you plan to remove this, test, test and test again (money click creating/modifying analyses changing charts like crazy)
    if (Object.keys(fullOptions).includes("yAxis"))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const s of chart.series) (s as any).destroy();

    chart.update(
      withNavigatorSeriesOptions(hasNavigator, fullOptions, normalizeNavigatorSeries),
      true,
      true,
      false,
    );
  } else if (optionsChanged) {
    // simple option update
    console.debug(
      `%c[${name}] %cOPTIONS-UPDATE`,
      `color: ${stableColor(name)};`,
      "color: pink;",
      Object.keys(optionsDiff).join(", "),
    );
    chart.update(optionsDiff, true, true);
  } else if (seriesChanged) {
    if (addedSeriesOptions.length) {
      console.debug(
        `%c[${name}] %cSERIES-ADDED`,
        `color: ${stableColor(name)};`,
        "color: green;",
        `[${addedSeriesOptions.map(s => s.id).join("|")}]`,
      );

      for (const serieOptions of addedSeriesOptions) {
        chart.addSeries(serieOptions, false);
        if (serieOptions.replicateInNavigator && hasNavigator) {
          chart.addSeries(
            {
              ...(navigatorSerieOptions(
                serieOptions as NavigatorSeriesOptions,
                normalizeNavigatorSeries,
                seriesOptions as NavigatorSeriesOptions[],
              ) as Highcharts.SeriesLineOptions),
              // https://www.highcharts.com/forum/viewtopic.php?t=33638
              xAxis: "navigator-x-axis",
              yAxis: "navigator-y-axis",
            },
            false,
          );
        }
      }
    }

    if (deletedSeriesOptions.length) {
      console.debug(
        `%c[${name}] %cSERIES-DELETED`,
        `color: ${stableColor(name)};`,
        "color: red;",
        `[${deletedSeriesOptions.map(s => s.id).join("|")}]`,
      );
      for (const serieOptions of deletedSeriesOptions) {
        if (!serieOptions.id) throw new Error(`Missing serieOptions.id`);
        findSerie(chart, serieOptions.id, false).remove(false);
        const navigatorSerie =
          serieOptions.replicateInNavigator && hasNavigator
            ? findSerie(chart, serieOptions.id, true)
            : null;
        navigatorSerie?.remove(false);
      }
    }

    const doNotAnimate = changedSeriesOptions.some(changedSerieOptions => {
      if (!changedSerieOptions.id) throw new Error(`Missing options.id`);
      const serie = findSerie(chart, changedSerieOptions.id, false);
      if (serie.type !== "line") return false;

      const oldIds =
        "data" in serie.options && Array.isArray(serie.options.data)
          ? serie.options.data.map(d => d.id)
          : [];
      const data = "data" in changedSerieOptions ? changedSerieOptions.data : null;

      const newIds = data && Array.isArray(data) ? data.map(d => d.id) : [];
      if (!oldIds.length || !newIds.length) {
        return false;
      }
      const [removed, added] = removeCommonElements(oldIds, newIds);
      return added.length + removed.length > 10;
    });

    for (const changedSerieOptions of changedSeriesOptions) {
      if (!changedSerieOptions.id) throw new Error(`Missing options.id`);
      smartSerieUpdate(
        name,
        chart,
        changedSerieOptions as Highcharts.SeriesLineOptions,
        normalizeNavigatorSeries,
        seriesOptions as Highcharts.SeriesLineOptions[],
      );
    }

    // TODO animation
    chart.redraw(!doNotAnimate);
  } else {
    throw new Error("What happened?");
  }
};

const smartSerieUpdate = (
  chartName: string,
  chart: Highcharts.Chart,
  { id, ...options }: Highcharts.SeriesLineOptions, // should work for others option types
  normalizeNavigatorSeries: boolean,
  seriesOptions: Highcharts.SeriesLineOptions[],
) => {
  const hasNavigator = Boolean(chart.options.navigator?.enabled);
  const { data, ...otherOptions } = options;
  if (!id) throw new Error("Missing id in smartSerieUpdate");
  const serie = findSerie(chart, id, false);
  const userOptionsSerie = serie.userOptions as Highcharts.SeriesLineOptions;

  const navigatorSerie =
    serie.userOptions.replicateInNavigator && hasNavigator ? findSerie(chart, id, true) : null;
  console.debug(
    `%c[${chartName}] %cSERIE-CHANGED`,
    `color: ${stableColor(chartName)};`,
    "color: orange;",
    `[${id}]: ${Object.keys(options).join("/")}`,
  );

  // other serie options or no data change
  if (!data) {
    serie.update(otherOptions, false);
    if (navigatorSerie)
      navigatorSerie.update(
        navigatorSerieOptions(
          { ...otherOptions, id },
          normalizeNavigatorSeries,
          seriesOptions,
        ) as Highcharts.SeriesLineOptions,
        false,
      );
    // data and options changed, update all
  } else if (Object.keys(otherOptions).length) {
    serie.update(options, false);
    if (navigatorSerie)
      navigatorSerie.update(
        navigatorSerieOptions(
          { ...options, id, yAxis: userOptionsSerie.yAxis },
          normalizeNavigatorSeries,
          seriesOptions,
        ) as Highcharts.SeriesLineOptions,
        false,
      );
  } else {
    // setData mutates data (even with updatePoints, the doc is incorrect)
    const clonedData = cloneDeep(data);
    serie.setData(clonedData, false, undefined, true);
    if (navigatorSerie)
      navigatorSerie.setData(
        normalizeData(
          { ...userOptionsSerie, data: clonedData, yAxis: userOptionsSerie.yAxis },
          normalizeNavigatorSeries,
          seriesOptions,
        ),
        false,
        false,
        true,
      );
  }
};

export default smartUpdate;
